import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Form, Row, Button, Tooltip, Modal, Input } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import moment from "moment";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { toDisplayFormat, toHttpFormat, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import { TableRecord } from "../../lib/interfaces/TableRecord";

const IndividualEmailsTable = () => {
  const {
    accountEmails,
    accountEmailsLoading,
    emailsVersions,
    emailsVersionsLoading,
  } = useSelector((state: RootStateOrAny) => state.accounts);

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const {
    fetchEmailVersions,
    createEmail,
    handleCreateEmailVersion,
    handleCloseEmailVersion,
  } = useAccountInfoContainerContext();

  useEffect(() => {
    if (checkIsLoaded(accountEmailsLoading)) {
      setTableData(
        accountEmails
          .map((item) => ({
            key: item.id,
            ...item,
          }))
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [accountEmailsLoading]);

  useEffect(() => {
    if (checkIsLoaded(emailsVersionsLoading)) {
      const flattedEmailsVersions = emailsVersions.flat();
      setChildTableData(
        flattedEmailsVersions
          .map((version, i) => ({
            ...version,
            ...versionsTableUtils.getCommonChildTableProps(
              version,
              i,
              flattedEmailsVersions.length
            ),
          }))
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [emailsVersionsLoading]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    email: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: columnsWidths.email,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: columnsWidths.email,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="email"
          record={record}
          setFieldsValue={form.setFieldsValue}
          rules={[{ required: true, message: "", type: "email" }]}
        />
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          toDateDisabled
          initialToDate={moment()}
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
              errorOptions={{
                value: tableData.length === 1,
                title: "Закрытие e-mail",
                content:
                  "Для корректной работы системы у аккаунта должен быть задан хотя бы один e-mail",
              }}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
    tableData,
  ]);

  const renderCreateModal = () => {
    const closeCreateModal = () => {
      setCreateModalVisible(false);
      createForm.resetFields();
    };
    const onFinishCreate = ({ email }) => {
      const createdEmails = [
        {
          fromDate: toHttpFormatStartOfDay(moment()),
          email,
        },
      ];
      createEmail(createdEmails);
      closeCreateModal();
    };
    return (
      <Modal
        title="Электронная почта"
        okText="Сохранить"
        visible={createModalVisible}
        onCancel={closeCreateModal}
        footer={[
          <Button key="back" onClick={closeCreateModal}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" onClick={createForm.submit}>
            Сохранить
          </Button>,
        ]}
      >
        <Form
          form={createForm}
          initialValues={{
            exiteId: "",
            date: "",
          }}
          onFinish={onFinishCreate}
          labelCol={{ span: 6, offset: 4 }}
        >
          <Form.Item
            name="email"
            className="form-item-required__hidden-error__mb0"
            wrapperCol={{ span: 16, offset: 4 }}
            rules={[{ required: true, type: "email", message: "Введите корректный e-mail" }]}
          >
            <Input allowClear />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const onFinish = (values) => {
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);

    if (changeableRecord.isClosing) {
      handleCloseEmailVersion(changeableRecord.id, toHttpFormat(values.toDate));
    } else {
      const force = Boolean(changeableRecord.isEditing);
      const fromDate = force
        ? String(changeableRecord.fromDate)
        : toHttpFormatStartOfDay(values.fromDate);
      const emailData = {
        id: changeableRecord.id,
        email: values.email,
      };
      handleCreateEmailVersion({
        fromDate,
        emailData,
        force,
      });
    }
  };

  return (
    <>
      {renderCreateModal()}
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          style={{ marginTop: "45px" }}
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Электронная почта</h4>
              <Tooltip title="Добавить электронную почту">
                <Button
                  style={{ color: "#00BB77", marginLeft: "15px", border: "none" }}
                  shape="circle"
                  icon={<PlusCircleFilled />}
                  onClick={() => setCreateModalVisible(true)}
                />
              </Tooltip>
            </Row>
          }
          tableName="individual-emails-table"
          columns={columns}
          setColumns={setColumns}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          tableData={tableData}
          childTableData={childTableData}
          isChildTableLoading={checkIsLoading(emailsVersionsLoading)}
          tableClassName="individual-emails-table"
          isPageable={false}
          isToggleableColumns={false}
          onOpenVersionsCallback={(record) => {
            fetchEmailVersions(record.id);
          }}
        />
      </Form>
    </>
  );
};

export default IndividualEmailsTable;
